<template>
    <nav v-if="open" ref="panelEl" class="vp-panel vp-chapters" noautohide :aria-label="t('Chapters')">
        <div class="vp-chapters-header">
            <span class="vp-label">{{ t("Chapters") }}</span>
            <button type="button" class="vp-icon-button" :aria-label="t('Close')" @click="emit('close')">
                <VideoIcon name="close" :size="18" />
            </button>
        </div>

        <ol class="vp-chapters-list">
            <li v-for="(chapter, index) in chapters" :key="chapter.id">
                <button
                    type="button"
                    class="vp-chapter"
                    :aria-current="index === activeIndex ? 'true' : undefined"
                    @click="emit('seek', chapter.start)"
                >
                    <img v-if="chapter.image" :src="chapter.image" alt="" class="vp-chapter-image" loading="lazy" />
                    <span class="vp-chapter-text">
                        <span class="vp-chapter-title">{{ t("Chapter {n}", { n: index + 1 }) }} · {{ chapter.text }}</span>
                        <span class="vp-mono vp-chapter-time">{{ formatClock(chapter.start) }}</span>
                    </span>
                </button>
            </li>
        </ol>
    </nav>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from "vue"
import VideoIcon from "./VideoIcon.vue"
import { formatClock } from "../format.js"
import { useCoreI18n } from "../useCoreI18n.js"

const props = defineProps({
    open: { type: Boolean, default: false },
    chapters: { type: Array, default: () => [] },
    activeIndex: { type: Number, default: -1 },
})

const emit = defineEmits(["seek", "close"])

const t = useCoreI18n()
const panelEl = ref(null)

// Touch devices have no mouseleave, so the panel closes on a pointerdown
// outside it (the toggle button excluded, it closes the panel itself) and on Escape.
function onOutsidePointerDown(event) {
    const path = event.composedPath()

    if (panelEl.value && !path.includes(panelEl.value) && !path.some((node) => node.dataset?.vpChaptersToggle !== undefined)) {
        emit("close")
    }
}

function onKeydown(event) {
    if (event.key === "Escape") {
        emit("close")
    }
}

function stopListening() {
    document.removeEventListener("pointerdown", onOutsidePointerDown, true)
    document.removeEventListener("keydown", onKeydown)
}

watch(
    () => props.open,
    (open) => {
        stopListening()

        if (open) {
            document.addEventListener("pointerdown", onOutsidePointerDown, true)
            document.addEventListener("keydown", onKeydown)
        }
    },
    { immediate: true },
)

onBeforeUnmount(stopListening)
</script>

<style>
.vp-chapters {
    position: absolute;
    z-index: 2;
    right: 16px;
    bottom: 112px;
    width: min(340px, calc(100% - 32px));
    max-height: calc(100% - 200px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.vp-chapters-header {
    padding: 4px 4px 4px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.vp-chapters-list {
    margin: 0;
    padding: 0 8px 8px;
    overflow-y: auto;
    list-style: none;
}

.vp-chapter {
    width: 100%;
    padding: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 10px;
    text-align: left;
    color: var(--vp-muted);
    background: transparent;
}

.vp-chapter:hover {
    background: rgba(251, 249, 245, 0.08);
}

.vp-chapter[aria-current="true"] {
    color: var(--vp-paper);
    box-shadow: inset 2px 0 0 var(--vp-accent);
}

.vp-chapter-image {
    width: 64px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 6px;
    object-fit: cover;
}

.vp-chapter-text {
    min-width: 0;
    flex-grow: 1;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
}

.vp-chapter-title {
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.vp-chapter-time {
    flex-shrink: 0;
    font-size: 12px;
    color: var(--vp-muted);
}

@media (max-height: 420px) {
    .vp-chapters {
        top: 8px;
        bottom: 8px;
        max-height: none;
    }
}
</style>
